import React from 'react';
import { Spinner } from 'react-bootstrap';

import Button from 'components/DesignSystem/Button';
import Icon from 'components/DesignSystem/Icon';
import TooltipWrapper from 'components/DesignSystem/TooltipWrapper';
import { WindxSimulacaoAntiga } from 'helper/windx';
import { cssVar } from 'constants/theme';

// Maximo de caracteres exibidos do nome antes de truncar com reticencias.
// Acima disso o usuario ve o nome completo no tooltip (hover).
const MAX_NOME_CHARS = 8;

interface SimulacaoItemProps {
  sim: WindxSimulacaoAntiga;
  ativa: boolean;
  /**
   * Quando true, o checkbox fica disabled (limite de simulacoes ativas
   * atingido). Apenas as ja ATIVAS conseguem ser desmarcadas — esta prop
   * cobre as INATIVAS quando o limite ja foi alcancado.
   */
  bloqueado: boolean;
  isDeleting: boolean;
  onToggle: (sim: WindxSimulacaoAntiga) => void;
  onEditar: (sim: WindxSimulacaoAntiga) => void;
  onDeletar: (sim: WindxSimulacaoAntiga) => void;
}

/**
 * Linha de uma simulacao antiga salva: checkbox + nome (truncado) + D-N + 2 botoes.
 *
 * FE-WINDX-043 v7: checkbox multi (era radio). Limite max 3 ativas enforced
 * tanto pelo reducer (toggleSimulacaoAntigaAtiva ignora a 4a) quanto pela
 * UI (prop `bloqueado` desabilita inativas quando ja ha 3).
 *
 * Bug PO 2026-05-27 #1: nome era exibido na linha de cima e `D-N` em uma
 * segunda linha (small text-muted). Agora os dois ficam na MESMA linha,
 * com o nome truncado a MAX_NOME_CHARS=8 + reticencias quando necessario.
 * Hover mostra nome completo via TooltipWrapper (azul marinho do DS, em
 * vez do tooltip preto nativo do navegador).
 *
 * - input[type=checkbox] nativo + label htmlFor (jsx-a11y compliant)
 * - Botoes edit/delete IRMAOS do label (evita event bubbling)
 * - TooltipWrapper do design system (azul marinho #26548A) substitui
 *   o atributo HTML `title` (que era preto nativo do browser)
 */
export default function SimulacaoItem({
  sim,
  ativa,
  bloqueado,
  isDeleting,
  onToggle,
  onEditar,
  onDeletar,
}: SimulacaoItemProps) {
  const inputId = `windx-sim-${sim.id}`;
  // Disabled apenas se nao ativa E bloqueado (ja atingiu limite). Ativas
  // sempre podem ser desmarcadas.
  const disabled = !ativa && bloqueado;
  const tooltipText = disabled
    ? 'Limite de 3 simulações antigas atingido. Desmarque uma para selecionar outra.'
    : `${sim.nome} — Simulação D-${sim.dias_atras}`;
  const nomeExibido = sim.nome.length > MAX_NOME_CHARS
    ? `${sim.nome.substring(0, MAX_NOME_CHARS)}...`
    : sim.nome;

  return (
    <div className="d-flex align-items-center mb-1">
      <input
        type="checkbox"
        id={inputId}
        name="windx-simulacao-antiga"
        checked={ativa}
        disabled={disabled}
        onChange={() => onToggle(sim)}
        className="me-2"
      />
      <TooltipWrapper title={tooltipText} placement="top" className="flex-grow-1">
        <label
          htmlFor={inputId}
          className="mb-0 d-flex align-items-center gap-2"
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          <span className={disabled ? 'text-muted' : ''}>{nomeExibido}</span>
          <small className="text-muted">{`D-${sim.dias_atras}`}</small>
        </label>
      </TooltipWrapper>
      <Button
        type="tertiary"
        className="btn-preference p-1"
        onClick={() => onEditar(sim)}
        aria-label={`Editar simulação ${sim.nome}`}
      >
        <Icon iconType="edit" color={cssVar('text-accent')} />
      </Button>
      <Button
        type="tertiary"
        className="btn-preference p-1"
        disabled={isDeleting}
        onClick={() => onDeletar(sim)}
        aria-label={`Excluir simulação ${sim.nome}`}
      >
        {isDeleting
          ? <Spinner size="sm" aria-hidden="true" />
          : <Icon iconType="trash" color={cssVar('text-accent')} />}
      </Button>
    </div>
  );
}
